import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import EmailHtml from 'assets/components/html-templates/email/EmailTemplate';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import { inputStyles } from 'assets/components/inputs/Input';
import RichTextInput from 'assets/components/inputs/richTextInput/RichTextInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import CheckWrapper from 'assets/components/inputs/wrappers/CheckWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import { combineStrings } from 'assets/utils/data/String';
import { isEmail, isEmailDomain } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import { difference, set, values } from 'lodash';
import providerStatementBalanceEmailTemplateDeleteAttachmentApi from 'models/core/providerStatementBalanceEmailTemplate/deleteAttachment/Api.ProviderStatementBalanceEmailTemplate.DeleteAttachment';
import providerStatementBalanceEmailTemplateSaveApi from 'models/core/providerStatementBalanceEmailTemplate/save/Api.ProviderStatementBalanceEmailTemplate.Save';
import Role from 'models/core/role/Model.Role';
import {
  entityTranslationDeleteApi,
  entityTranslationListApi,
  entityTranslationSaveApi,
} from 'models/localization/entityTranslation/Model.EntityTranslation';
import ProviderStatementBalanceEmailTemplateTags from 'module/management/components/providerStatementBalanceEmailTemplate/ProviderStatementBalanceEmailTemplateTags';
import { renderToString } from 'react-dom/server';
import { useProviderStatementBalanceEmailTemplateProvider } from '../ProviderStatementBalanceEmailTemplate.Hooks';
import { ProviderStatementBalanceEmailTemplateEditTranslation } from './edit/ProviderStatementBalanceEmailTemplate.Edit.Translation';
import providerStatementBalanceEmailTemplateUploadAttachmentApi from 'models/core/providerStatementBalanceEmailTemplate/uploadAttachment/Api.ProviderStatementBalanceEmailTemplate.UploadAttachment';

export type TranslatedProviderStatementBalanceEmailTemplate = Api.Core.EntityTranslation.Save.IRequest & {
  isRemoved?: boolean;
};
export type ProviderStatementBalanceEmailTemplateForm = Omit<
  Api.Core.ProviderStatementBalanceEmailTemplate.Save.IRequest,
  'attachments'
> & {
  attachments: Hooks.Input.File.Info[];
  translatedTemplates: TranslatedProviderStatementBalanceEmailTemplate[];
};
export default function ProviderStatementBalanceEmailTemplateEdit() {
  const { lang, currentLanguage } = getLocales();
  const { selected, pageRouter, availableStatuses, listApi, providerListApi, statementBalanceEmailTemplates } =
    useProviderStatementBalanceEmailTemplateProvider();
  const { payload: rolesPayload } = useApi({ action: Role.list });
  const stringTranslationsApi = useApi(
    {
      action: entityTranslationListApi,
      body: { entityType: 'ProviderStatementBalanceEmailTemplate', entityId: [selected?.id] },
      wait: !selected?.id,
    },
    [selected]
  );

  return (
    <>
      <ModalForm<ProviderStatementBalanceEmailTemplateForm>
        subcomponents={ThemeModalFormDefault}
        dependencyArray={[selected, stringTranslationsApi?.payload, statementBalanceEmailTemplates?.data]}
        title={lang.emailTemplate}
        default={
          selected
            ? {
                ...selected.toJSON(),
                languageIsoCode: selected.languageIsoCode ?? Language.english,
                attachments: selected.attachments?.map((it) => ({
                  id: it.id,
                  dataUrl: it.mediaFile.url,
                  name: it.mediaFile.name,
                  type: it.mediaFile.contentType as Utils.File.MimeType,
                  languageIsoCode: it.languageIsoCode ?? selected.languageIsoCode ?? currentLanguage,
                })),
                statementBalanceEmailTemplate: statementBalanceEmailTemplates?.data?.find(
                  (it) => it.id === selected?.statementBalanceEmailTemplateId
                ),
                translatedTemplates: stringTranslationsApi?.payload?.data?.filter((it) => it.entityId === selected?.id),
              }
            : {
                htmlBody: renderToString(EmailHtml({ body: 'Message' })),
                additionalRecipientEmails: [],
                attachments: [],
                languageIsoCode: Language.english,
                translatedTemplates: [],
              }
        }
        onCancel={pageRouter.updateParams}
        onSubmit={async ({ attachments, translatedTemplates, ...data }) => {
          data.provider = undefined;
          data.statementBalanceEmailTemplate = undefined;
          const res = await providerStatementBalanceEmailTemplateSaveApi(data);

          if (res.status === HttpStatus.ok) {
            const templateId = res?.payload;

            let totalFiles = 0;
            let uploadedFiles = 0;
            let deletedFiles = 0;
            let totalDeletedFiles = 0;
            const deletedFileIds = difference(
              selected?.attachments?.map((it) => it.id),
              attachments?.map((it) => it.id)
            );
            for (const fileId of deletedFileIds || []) {
              const fileUploadResponse = await providerStatementBalanceEmailTemplateDeleteAttachmentApi(templateId, {
                fileId,
              });
              if (fileUploadResponse.status === HttpStatus.ok) {
                deletedFiles += 1;
              }
              totalDeletedFiles += 1;
            }
            for (const formFile of attachments?.filter((it) => !it.id && it.original) || []) {
              const fileUploadResponse = await providerStatementBalanceEmailTemplateUploadAttachmentApi(templateId, {
                formFile: formFile.original,
                languageIsoCode: Language.english,
              });
              if (fileUploadResponse.status === HttpStatus.ok) {
                uploadedFiles += 1;
              }
              totalFiles += 1;
            }
            if (uploadedFiles !== totalFiles || deletedFiles !== totalDeletedFiles) {
              addLog({
                warning: combineStrings(
                  ' ',
                  `Some attachments did not get deleted or uploaded!`,
                  totalFiles > 0 ? `${uploadedFiles}/${totalFiles} attachments uploaded` : null,
                  totalDeletedFiles > 0 ? `${deletedFiles}/${totalDeletedFiles} attachments got deleted!` : null
                ),
              });
            } else {
              addLog({ success: lang.saveSuccess });
            }

            for (const translatedTemplate of translatedTemplates) {
              if (translatedTemplate?.isRemoved && translatedTemplate?.id) {
                await entityTranslationDeleteApi({
                  id: translatedTemplate?.id,
                });
              } else {
                await entityTranslationSaveApi({
                  ...translatedTemplate,
                  entityId: templateId,
                });
              }
            }
          } else {
            addLog({ error: res.message });
          }
          listApi.execute((body) => body);
          pageRouter.updateParams();
        }}
        validation={(data, errors) => {
          if (
            !data.additionalRecipientEmails?.some((it) => it) &&
            !data.recipientsWithRole?.some((it) => it.id) &&
            !data.sendToDealersAccounting &&
            !data.sendToRepresentative
          )
            set(errors, 'additionalRecipientEmails', lang.mustNotBeEmpty);
          if (data.additionalRecipientEmails?.length && data.additionalRecipientEmails?.some((it) => !isEmail(it)))
            set(errors, 'additionalRecipientEmails', lang.invalidEmailFormatRequires);
          if (!data.subject) set(errors, 'subject', lang.mustNotBeEmpty);
          if (!data.senderEmail) set(errors, 'senderEmail', lang.mustNotBeEmpty);
          if (data.senderEmail && !isEmailDomain(data.senderEmail, 'goautoshield.com'))
            set(errors, 'senderEmail', lang.mustBeEmailDomain.replace('{domain}', 'goautoshield.com'));
          if (!data.name) set(errors, 'name', lang.mustNotBeEmpty);
          if (!data.senderName) set(errors, 'senderName', lang.mustNotBeEmpty);
          if (!data.htmlBody) set(errors, 'htmlBody', lang.mustNotBeEmpty);
          if (!data.statementBalanceStatusTrigger) set(errors, 'statementBalanceStatusTrigger', lang.mustNotBeEmpty);
          if (!data.statementBalanceEmailTemplateId)
            set(errors, 'statementBalanceEmailTemplateId', lang.mustNotBeEmpty);
          if (!data.providerId) set(errors, 'providerId', lang.mustNotBeEmpty);

          if (data.statementBalanceStatusTrigger !== data?.statementBalanceEmailTemplate?.statementBalanceStatusTrigger)
            set(
              errors,
              'statementBalanceEmailTemplateId',
              lang.mustBEqualToValue.replace(
                '{value}',
                `${lang.statementBalanceStatusTrigger} ${data.statementBalanceStatusTrigger}`
              )
            );
        }}
        render={({ form }) => (
          <>
            <FormWrapper>
              <TextInput
                name="name"
                label={lang.name}
                value={form.data.name}
                error={form.errors.name}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <InputWrapper>
                <TextInput
                  name="senderName"
                  label={lang.senderName}
                  value={form.data.senderName}
                  error={form.errors.senderName}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
                <TextInput
                  name="senderEmail"
                  label={lang.senderEmail}
                  value={form.data.senderEmail}
                  error={form.errors.senderEmail}
                  onChange={form.update}
                  icon={<Icon class="if-quill-pen" />}
                />
              </InputWrapper>
              <SelectInput
                name="statementBalanceStatusTrigger"
                label={lang.statementBalanceStatusTrigger}
                data={availableStatuses?.map((status) => ({
                  id: status,
                  title: (lang[status] ?? status)?.replace(/_/g, ' - '),
                  icon: <Icon class="fas-box" />,
                }))}
                value={form.data.statementBalanceStatusTrigger}
                error={form.errors.statementBalanceStatusTrigger}
                onChange={form.update}
                icon={<Icon class="if-cloud" />}
              />
              <InputWrapper>
                <SelectInput
                  name="providerId"
                  label={lang.provider}
                  data={providerListApi?.payload?.data?.map((it) => it.displayInfo)}
                  value={form.data.providerId}
                  error={form.errors.providerId}
                  onChange={({ providerId }) => {
                    form.update({
                      providerId,
                      provider: providerListApi?.payload?.data?.find((it) => it.id === providerId),
                    });
                  }}
                  icon={<Icon class="fas-clipboard-list" />}
                />
                <SelectInput
                  name="statementBalanceEmailTemplateId"
                  label={lang.statementBalanceEmailTemplates}
                  data={statementBalanceEmailTemplates?.data?.map((it) => it.displayInfo)}
                  value={form.data.statementBalanceEmailTemplateId}
                  error={form.errors.statementBalanceEmailTemplateId}
                  onChange={({ statementBalanceEmailTemplateId }) => {
                    form.update({
                      statementBalanceEmailTemplateId,
                      statementBalanceEmailTemplate: statementBalanceEmailTemplates?.data?.find(
                        (it) => it.id === statementBalanceEmailTemplateId
                      ),
                    });
                  }}
                  icon={<Icon class="fas-clipboard-list" />}
                />
              </InputWrapper>
              <h2>{lang.sendTo}</h2>
              <CheckWrapper>
                <CheckInput
                  name="sendToRepresentative"
                  label={lang.representative}
                  class={inputStyles.check.switch}
                  value={form.data?.sendToRepresentative}
                  onChange={form.update}
                  htmlElementProps={{ title: form.data?.sendToRepresentative ? lang.disable : lang.enable }}
                />
                <CheckInput
                  name="sendToDealersAccounting"
                  label={lang.dealersAccounting}
                  class={inputStyles.check.switch}
                  value={form.data?.sendToDealersAccounting}
                  onChange={form.update}
                  htmlElementProps={{ title: form.data?.sendToDealersAccounting ? lang.disable : lang.enable }}
                />
              </CheckWrapper>
              &nbsp;
              <SelectInput
                name="recipientsWithRole"
                label={lang.recipientsWithRole}
                data={rolesPayload?.data?.map((item) => ({
                  ...item.displayInfo,
                }))}
                value={form.data.recipientsWithRole?.map((it) => it.id)}
                isMultiselect
                onChange={({ recipientsWithRole: id }) =>
                  form.update({ recipientsWithRole: id?.map((id) => ({ id })) })
                }
                icon={<Icon class="if-cloud" />}
              />
              <h2>{lang.additionalRecipientEmails}</h2>
              <DataMap
                data={[...(form.data.additionalRecipientEmails || []), '']}
                render={({ data: email, index }) => (
                  <TextInput
                    key={index}
                    name="email"
                    label={lang.email}
                    value={email}
                    error={form.errors.additionalRecipientEmails}
                    onChange={({ email }) => {
                      let additionalRecipientEmails = form.data.additionalRecipientEmails || [];
                      additionalRecipientEmails.splice(index, 1, email);
                      additionalRecipientEmails = additionalRecipientEmails.filter((it) => it);
                      form.update({ additionalRecipientEmails });
                    }}
                    icon={<Icon class="if-quill-pen" />}
                  />
                )}
              />
              <FileInput
                name="attachments"
                label={lang.attachments}
                value={form.data?.attachments || []}
                onChange={form.update}
                class={ThemeFileInputDropzone}
              />
              <h2>{lang.email}</h2>
              <TextInput
                name="subject"
                label={lang.subject}
                value={form.data.subject}
                error={form.errors.subject}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
              />
              <RichTextInput
                name="htmlBody"
                label={lang.htmlBody}
                value={form.data.htmlBody}
                error={form.errors.htmlBody}
                onChange={form.update}
                icon={<Icon class="if-quill-pen" />}
                customActions={<ProviderStatementBalanceEmailTemplateTags />}
              />
              <div style={{ display: 'flex', flexDirection: 'column', gap: Theme.Size.M }}>
                <DataMap
                  data={values(Language).filter((l) => l !== Language.english)}
                  render={({ data: languageIsoCode }) => (
                    <ProviderStatementBalanceEmailTemplateEditTranslation
                      key={languageIsoCode}
                      languageIsoCode={languageIsoCode}
                    />
                  )}
                />
              </div>
            </FormWrapper>
          </>
        )}
      />
    </>
  );
}
